import React, { useEffect, useState } from 'react';
import api from '../services/api';

const containerStyle = {
  maxWidth: '720px',
  background: '#ffffff',
  padding: '24px',
  borderRadius: '12px',
  border: '1px solid #e2e8f0',
};

const sectionTitle = {
  fontSize: '1.1rem',
  fontWeight: 600,
  marginBottom: '12px',
};

const fieldGroup = {
  display: 'flex',
  flexDirection: 'column',
  gap: '6px',
  marginBottom: '16px',
};

const labelStyle = {
  fontWeight: 600,
  color: '#334155',
};

const inputStyle = {
  padding: '10px 12px',
  borderRadius: '8px',
  border: '1px solid #cbd5f5',
  fontSize: '0.95rem',
};

const buttonStyle = {
  padding: '12px 18px',
  background: '#2563eb',
  color: '#fff',
  border: 'none',
  borderRadius: '8px',
  cursor: 'pointer',
  fontWeight: 600,
  marginTop: '12px',
};

const infoBox = {
  marginTop: '16px',
  padding: '12px 16px',
  borderRadius: '8px',
  fontSize: '0.9rem',
  lineHeight: 1.6,
};

function PayrollSettingsPage() {
  const [settings, setSettings] = useState(null);
  const [form, setForm] = useState({
    companyName: '',
    businessNumber: '',
    address: '',
    phone: '',
    representative: '',
    baseHourlyRate: '',
    weeklyAllowanceRate: '',
    overtimeHourlyRate: '',
    annualAllowanceRate: '',
  });
  const [stampFile, setStampFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [existingStamp, setExistingStamp] = useState('');
  const [message, setMessage] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    return () => {
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [previewUrl]);

  const fetchSettings = async () => {
    try {
      const response = await api.get('/payroll/settings/payslip');
      const data = response.data || {};
      setSettings(data);
      setForm({
        companyName: data.companyName || '',
        businessNumber: data.businessNumber || '',
        address: data.address || '',
        phone: data.phone || '',
        representative: data.representative || '',
        baseHourlyRate: data.rates?.baseHourlyRate || '',
        weeklyAllowanceRate: data.rates?.weeklyAllowanceRate || '',
        overtimeHourlyRate: data.rates?.overtimeHourlyRate || '',
        annualAllowanceRate: data.rates?.annualAllowanceRate || '',
      });
      setExistingStamp(data.stampImagePath || '');
    } catch (error) {
      console.error(error);
      setMessage({ type: 'error', text: '설정을 불러오지 못했습니다.' });
    }
  };

  useEffect(() => {
    fetchSettings();
  }, []);

  const handleChange = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const formData = new FormData();
    Object.entries(form).forEach(([key, value]) => {
      formData.append(key, value);
    });
    if (stampFile) {
      formData.append('stampImage', stampFile);
    }

    try {
      setLoading(true);
      const response = await api.put('/payroll/settings/payslip', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setMessage({ type: 'success', text: response.data.message });
      setStampFile(null);
      setPreviewUrl(null);
      setExistingStamp(response.data.settings?.stampImagePath || '');
      fetchSettings();
    } catch (error) {
      const text = error.response?.data?.message || '저장에 실패했습니다.';
      setMessage({ type: 'error', text });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <h2 style={{ marginBottom: '16px' }}>급여 명세표 설정</h2>
      <form style={containerStyle} onSubmit={handleSubmit}>
        <div style={sectionTitle}>사업장 정보</div>
        <div style={fieldGroup}>
          <label style={labelStyle}>사업장명</label>
          <input
            style={inputStyle}
            value={form.companyName}
            onChange={(e) => handleChange('companyName', e.target.value)}
            placeholder="예: 원주시니어클럽"
          />
        </div>
        <div style={fieldGroup}>
          <label style={labelStyle}>사업자번호</label>
          <input
            style={inputStyle}
            value={form.businessNumber}
            onChange={(e) => handleChange('businessNumber', e.target.value)}
            placeholder="예: 224-82-63515"
          />
        </div>
        <div style={fieldGroup}>
          <label style={labelStyle}>사업장 주소</label>
          <input
            style={inputStyle}
            value={form.address}
            onChange={(e) => handleChange('address', e.target.value)}
            placeholder="예: 강원도 원주시 ..."
          />
        </div>
        <div style={fieldGroup}>
          <label style={labelStyle}>전화번호</label>
          <input
            style={inputStyle}
            value={form.phone}
            onChange={(e) => handleChange('phone', e.target.value)}
          />
        </div>
        <div style={fieldGroup}>
          <label style={labelStyle}>대표자</label>
          <input
            style={inputStyle}
            value={form.representative}
            onChange={(e) => handleChange('representative', e.target.value)}
          />
        </div>

        <div style={{ ...sectionTitle, marginTop: '24px' }}>시간당 단가 (안내 문구 자동 표기)</div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px' }}>
          <div style={fieldGroup}>
            <label style={labelStyle}>근무단가 (시간당)</label>
            <input
              type="number"
              style={inputStyle}
              value={form.baseHourlyRate}
              onChange={(e) => handleChange('baseHourlyRate', e.target.value)}
            />
          </div>
          <div style={fieldGroup}>
            <label style={labelStyle}>주휴수당 단가 (시간당)</label>
            <input
              type="number"
              style={inputStyle}
              value={form.weeklyAllowanceRate}
              onChange={(e) => handleChange('weeklyAllowanceRate', e.target.value)}
            />
          </div>
          <div style={fieldGroup}>
            <label style={labelStyle}>시간외수당 단가 (시간당)</label>
            <input
              type="number"
              style={inputStyle}
              value={form.overtimeHourlyRate}
              onChange={(e) => handleChange('overtimeHourlyRate', e.target.value)}
            />
          </div>
          <div style={fieldGroup}>
            <label style={labelStyle}>연차수당 단가 (시간당)</label>
            <input
              type="number"
              style={inputStyle}
              value={form.annualAllowanceRate}
              onChange={(e) => handleChange('annualAllowanceRate', e.target.value)}
            />
          </div>
        </div>

        <div style={{ ...sectionTitle, marginTop: '24px' }}>도장 이미지</div>
        <div style={fieldGroup}>
          <label style={labelStyle}>도장 파일 (PNG/JPG)</label>
          <input
            type="file"
            accept="image/png,image/jpeg"
            onChange={(e) => {
              const file = e.target.files?.[0];
              setStampFile(file || null);
              if (file) {
                if (previewUrl) {
                  URL.revokeObjectURL(previewUrl);
                }
                const url = URL.createObjectURL(file);
                setPreviewUrl(url);
              } else {
                if (previewUrl) {
                  URL.revokeObjectURL(previewUrl);
                }
                setPreviewUrl(null);
              }
            }}
          />
        </div>
        {existingStamp && !previewUrl && (
          <div style={{ marginBottom: '12px', color: '#64748b', fontSize: '0.85rem' }}>
            현재 등록된 도장: {existingStamp.split(/[/\\]/).pop()}
          </div>
        )}
        {previewUrl && (
          <div style={{ marginBottom: '16px' }}>
            <div style={{ marginBottom: '6px', color: '#64748b', fontSize: '0.85rem' }}>미리보기</div>
            <img src={previewUrl} alt="stamp preview" style={{ maxWidth: '160px', border: '1px solid #e2e8f0' }} />
          </div>
        )}

        <button type="submit" style={buttonStyle} disabled={loading}>
          {loading ? '저장 중...' : '설정 저장'}
        </button>

        {message && (
          <div
            style={{
              ...infoBox,
              background: message.type === 'error' ? '#fef2f2' : '#ecfdf5',
              border: `1px solid ${message.type === 'error' ? '#fca5a5' : '#a7f3d0'}`,
              color: message.type === 'error' ? '#b91c1c' : '#047857',
            }}
          >
            {message.text}
          </div>
        )}
      </form>

      <div
        style={{
          ...infoBox,
          marginTop: '24px',
          background: '#f1f5f9',
          border: '1px solid #cbd5f5',
          color: '#475569',
        }}
      >
        • 급여명세표는 PDF로 생성되며, 생년월일(YYMMDD)로 암호가 설정됩니다.
        <br />
        • 단가를 입력하면 급여명세표 하단의 계산방법에 자동 반영되어 역산된 시간/횟수가 표시됩니다.
        <br />
        • 도장 이미지는 옵션이며, 미등록 시 빈칸으로 출력됩니다.
        <br />
        • 한글 폰트는 시스템 내 기본 폴더에 있는 파일을 자동으로 사용합니다.
      </div>
    </div>
  );
}

export default PayrollSettingsPage;

